/**
 * ============================================================================
 * APP SWITCHER COMPONENT
 * ============================================================================
 *
 * An overlay that shows all currently-minimized windows so users can quickly
 * jump back to any "backgrounded" app. On desktop it renders a responsive
 * grid of live window previews; on mobile it renders a full-screen
 * horizontal carousel ("lazy susan").
 *
 * Previews are produced by Window.tsx: when a window is minimized AND a
 * `previewRect` prop is supplied, the window content is rendered at full
 * native size and CSS-transformed into that rect. This component measures
 * each slot and reports the resulting rects back to Desktop via
 * `onRectsChange` so Desktop can pass them to <Window>.
 */

"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDesktopContext } from "../contexts/DesktopContext";
import { useThemeOptional } from "../contexts/ThemeContext";
import type { DesktopIconConfig, WindowInstance } from "../types";
import { isWindowMinimized } from "../types";

export type AppSwitcherSlot = {
  element: HTMLElement;
  x: number;
  y: number;
  width: number;
  height: number;
};

export interface AppSwitcherProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  icons: DesktopIconConfig[];
  /**
   * Called whenever the per-window slot DOM nodes / sizes change. Desktop
   * stores these and threads them into <Window previewPortal={...}>, so
   * the live window contents are portaled *inside* the slot and move with
   * it on the compositor — no JS-driven scroll tracking, no lag.
   */
  onSlotsChange: (slots: Map<string, AppSwitcherSlot>) => void;
}

/* ==========================================================================
   ROOT
   ========================================================================== */

export function AppSwitcher({
  isOpen,
  onClose,
  isMobile,
  icons,
  onSlotsChange,
}: AppSwitcherProps) {
  const { windows, restoreWindow, closeWindow } = useDesktopContext();
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;

  const minimized = useMemo((): WindowInstance[] => {
    const out: WindowInstance[] = [];
    for (const instance of windows.values()) {
      if (isWindowMinimized(instance.state)) out.push(instance);
    }
    // Stable order by z-index so most-recently-minimized is first
    out.sort((a, b) => {
      const za = a.state.status === "minimized" ? a.state.zIndex : 0;
      const zb = b.state.status === "minimized" ? b.state.zIndex : 0;
      return zb - za;
    });
    return out;
  }, [windows]);

  const iconByWindowId = useMemo(() => {
    const m = new Map<string, DesktopIconConfig>();
    for (const i of icons) m.set(i.windowId, i);
    return m;
  }, [icons]);

  const handleSelect = useCallback(
    (windowId: string) => {
      restoreWindow(windowId);
      onClose();
    },
    [restoreWindow, onClose]
  );

  const handleCardClose = useCallback(
    (windowId: string) => {
      closeWindow(windowId);
      // If this was the last background app, close the overlay — no point
      // keeping an empty switcher panel on screen.
      if (minimized.length <= 1) onClose();
    },
    [closeWindow, minimized.length, onClose]
  );

  const handleKillAll = useCallback(() => {
    for (const instance of minimized) {
      closeWindow(instance.config.id);
    }
    onClose();
  }, [closeWindow, minimized, onClose]);

  // Escape closes
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  // When closed, clear all reported slots so the minimized windows return
  // to their hidden 1×1 state.
  useEffect(() => {
    if (!isOpen) onSlotsChange(new Map());
  }, [isOpen, onSlotsChange]);

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[220] flex ${isMobile ? "flex-col" : "items-center justify-center"}`}
      /* On desktop the outside is fully transparent — it's a popup, not a
         full-screen overlay. Mobile stays dimmed so the carousel reads as
         an immersive switcher. */
      style={
        isMobile
          ? {
              backgroundColor: isDark
                ? "rgba(0,0,0,0.55)"
                : "rgba(20,20,20,0.45)",
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
            }
          : undefined
      }
      onClick={onClose}
    >
      <div
        className={`flex flex-col overflow-hidden ${
          isMobile
            ? "h-full w-full"
            : "h-[75vh] w-[75vw] max-w-[1400px] rounded-2xl border border-white/20"
        }`}
        style={
          isMobile
            ? undefined
            : {
                /* Liquid-glass panel: low-opacity tint + heavy backdrop
                   blur so the desktop shows through but everything under
                   the panel gets frosted. Light top-edge highlight for
                   the "glass" feel. Soft drop shadow anchors it. */
                backgroundColor: isDark
                  ? "rgba(45,45,50,0.55)"
                  : "rgba(255,255,255,0.45)",
                backdropFilter: "blur(40px) saturate(180%)",
                WebkitBackdropFilter: "blur(40px) saturate(180%)",
                boxShadow: isDark
                  ? "0 40px 120px -20px rgba(0,0,0,0.75), 0 20px 60px -10px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.12)"
                  : "0 40px 120px -20px rgba(0,0,0,0.45), 0 20px 60px -10px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.6)",
              }
        }
        onClick={isMobile ? undefined : (e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pb-2 pt-4">
          <h2
            className="text-sm font-semibold tracking-wide"
            style={{
              color: isDark
                ? "rgba(255,255,255,0.9)"
                : "rgba(255,255,255,0.95)",
            }}
          >
            Background Apps
            <span
              className="ml-2 text-xs font-normal"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              ({minimized.length})
            </span>
          </h2>
          <div className="flex items-center gap-2">
            {minimized.length > 0 && (
              <button
                onClick={handleKillAll}
                className="rounded-full px-3 py-1 text-xs font-semibold transition-colors"
                style={{
                  background: "rgba(239,68,68,0.85)",
                  color: "rgba(255,255,255,0.98)",
                  boxShadow:
                    "0 1px 2px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.2)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "rgba(220,38,38,0.95)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "rgba(239,68,68,0.85)";
                }}
                aria-label="Close all background apps"
              >
                Kill All
              </button>
            )}
            <button
              onClick={onClose}
              className="rounded-full px-3 py-1 text-xs font-medium transition-colors"
              style={{
                background: "rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.85)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.18)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.1)";
              }}
              aria-label="Close app switcher"
            >
              {isMobile ? "Close" : "Close (Esc)"}
            </button>
          </div>
        </div>

        {minimized.length === 0 ? (
          <EmptyState />
        ) : isMobile ? (
          <AppSwitcherCarousel
            minimized={minimized}
            iconByWindowId={iconByWindowId}
            onSelect={handleSelect}
            onCardClose={handleCardClose}
            onSlotsChange={onSlotsChange}
          />
        ) : (
          <AppSwitcherGrid
            minimized={minimized}
            iconByWindowId={iconByWindowId}
            onSelect={handleSelect}
            onCardClose={handleCardClose}
            onSlotsChange={onSlotsChange}
          />
        )}
      </div>
    </div>
  );
}

/* ==========================================================================
   EMPTY STATE
   ========================================================================== */

function EmptyState() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <div className="flex flex-col items-center gap-2 text-center">
        <div className="text-4xl opacity-60">🪟</div>
        <div
          className="text-base font-semibold"
          style={{ color: "rgba(255,255,255,0.9)" }}
        >
          No background apps
        </div>
        <div className="text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
          Minimize a window to stash it here.
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   SLOT — measures its own rect and reports it up
   ========================================================================== */

interface SlotProps {
  windowId: string;
  title: string;
  icon: React.ReactNode;
  onSelect: () => void;
  onClose: () => void;
  onMeasure: (id: string, slot: AppSwitcherSlot | null) => void;
  /**
   * Bumped by the parent whenever the set of minimized windows changes.
   * ResizeObserver doesn't catch position shifts caused by a sibling
   * being removed from the grid/carousel, so we include this in the
   * measurement effect's deps to force a re-publish on those events.
   */
  layoutVersion: number;
  /** Additional wrapper className for layout (grid cell vs. carousel item) */
  className?: string;
  style?: React.CSSProperties;
  /**
   * Override style applied to the preview box (the clickable card).
   * Defaults to 16:10 aspect ratio for the desktop grid; mobile supplies
   * explicit portrait dimensions to mimic a mini-phone.
   */
  previewBoxStyle?: React.CSSProperties;
  /**
   * A changing value that indicates the slot's viewport position has
   * shifted without a scroll/resize event (e.g. the mobile swipe-to-close
   * drag translating the wrapper). When this changes we re-read the rect
   * so the portaled window preview tracks the wrapper.
   */
  liveOffset?: number;
}

function PreviewSlot({
  windowId,
  title,
  icon,
  onSelect,
  onClose,
  onMeasure,
  layoutVersion,
  className,
  style,
  previewBoxStyle,
  liveOffset = 0,
}: SlotProps) {
  const slotRef = useRef<HTMLDivElement>(null);
  const publishRef = useRef<() => void>(() => {});

  useLayoutEffect(() => {
    const el = slotRef.current;
    if (!el) {
      onMeasure(windowId, null);
      return;
    }

    // We publish the slot's full viewport rect (x/y + width/height). The
    // Window portals its content into a STABLE target (document.body) and
    // overlays this rect with position:fixed — so we need to republish on
    // anything that can shift the slot's viewport position, not just size.
    // Capture-phase scroll catches the mobile carousel's internal scroll
    // without needing to know which ancestor is the scroll container.
    const publish = () => {
      const r = el.getBoundingClientRect();
      onMeasure(windowId, {
        element: el,
        x: r.x,
        y: r.y,
        width: r.width,
        height: r.height,
      });
    };

    publishRef.current = publish;
    publish();

    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener("resize", publish);
    window.addEventListener("scroll", publish, {
      passive: true,
      capture: true,
    });

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", publish);
      window.removeEventListener("scroll", publish, { capture: true });
      onMeasure(windowId, null);
    };
  }, [windowId, onMeasure, layoutVersion]);

  // Drag-to-close on mobile translates the wrapper on the compositor; no
  // scroll/resize fires, so the portaled preview stays put unless we
  // republish the rect ourselves whenever the live offset changes.
  useLayoutEffect(() => {
    publishRef.current();
  }, [liveOffset]);

  return (
    <div className={className} style={style}>
      {/* Info row — app icon, title, and close button (above the preview) */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {/*
            App icon components render at their native size (~56px) with
            their own gradient/solid backgrounds. Downscaling via width
            constraints clips the background and shows only the inner glyph,
            so we render at native size and CSS-transform the whole node to
            preserve the branded colors.
          */}
          <div className="relative h-7 w-7 flex-none overflow-hidden">
            {typeof icon === "string" ? (
              <span className="flex h-full w-full items-center justify-center text-lg leading-none">
                {icon}
              </span>
            ) : (
              <div
                className="absolute left-1/2 top-1/2"
                style={{ transform: "translate(-50%, -50%) scale(0.5)" }}
              >
                {icon}
              </div>
            )}
          </div>
          <div
            className="truncate text-sm font-medium"
            style={{ color: "rgba(255,255,255,0.92)" }}
          >
            {title}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          className="flex h-6 w-6 flex-none items-center justify-center rounded-full transition-colors"
          style={{
            background: "rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.9)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(239,68,68,0.85)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.12)";
          }}
          aria-label={`Close ${title}`}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path
              d="M1.5 1.5 L8.5 8.5 M8.5 1.5 L1.5 8.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <button
        onClick={onSelect}
        className="group relative block overflow-hidden rounded-xl border border-white/10 bg-black/30 text-left transition-transform hover:scale-[1.02]"
        style={{ aspectRatio: "16 / 10", width: "100%", ...previewBoxStyle }}
      >
        {/*
          Fallback card — rendered BEHIND the portal target. When the live
          preview paints correctly, it sits on top and fully covers this.
          When the preview fails to paint (the production mobile bug), this
          card shows through instead of a black rectangle. The Window.tsx
          hidden-mounted wrapper runs with a transparent background so
          transparent areas of the portaled subtree reveal this layer.
        */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-white/[0.04] to-white/[0.08] px-4"
        >
          <div className="relative h-14 w-14 flex-none overflow-hidden">
            {typeof icon === "string" ? (
              <span className="flex h-full w-full items-center justify-center text-4xl leading-none">
                {icon}
              </span>
            ) : (
              <div
                className="absolute left-1/2 top-1/2"
                style={{ transform: "translate(-50%, -50%) scale(1)" }}
              >
                {icon}
              </div>
            )}
          </div>
          <div
            className="max-w-full truncate text-center text-sm font-medium"
            style={{ color: "rgba(255,255,255,0.7)" }}
          >
            {title}
          </div>
        </div>
        {/* Preview slot — Window.tsx transforms its content into this rect */}
        <div ref={slotRef} className="absolute inset-0" />
        {/* Hover overlay — pointer events on this sibling, not the slot */}
        <div className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-transparent transition-all group-hover:ring-white/60" />
      </button>
    </div>
  );
}

/* ==========================================================================
   SLOT-LIST helper — aggregates per-slot measurements for the parent
   ========================================================================== */

function useMeasureAggregator(
  onSlotsChange: (slots: Map<string, AppSwitcherSlot>) => void
) {
  const slotsRef = useRef<Map<string, AppSwitcherSlot>>(new Map());

  const publish = useCallback(() => {
    // Send a fresh Map so React sees a new reference.
    onSlotsChange(new Map(slotsRef.current));
  }, [onSlotsChange]);

  const onMeasure = useCallback(
    (id: string, slot: AppSwitcherSlot | null) => {
      if (slot === null) {
        slotsRef.current.delete(id);
      } else {
        slotsRef.current.set(id, slot);
      }
      publish();
    },
    [publish]
  );

  return onMeasure;
}

/* ==========================================================================
   GRID (desktop)
   ========================================================================== */

interface LayoutProps {
  minimized: WindowInstance[];
  iconByWindowId: Map<string, DesktopIconConfig>;
  onSelect: (id: string) => void;
  onCardClose: (id: string) => void;
  onSlotsChange: (slots: Map<string, AppSwitcherSlot>) => void;
}

function AppSwitcherGrid({
  minimized,
  iconByWindowId,
  onSelect,
  onCardClose,
  onSlotsChange,
}: LayoutProps) {
  const onMeasure = useMeasureAggregator(onSlotsChange);

  return (
    <div className="flex-1 overflow-auto px-8 pb-10 pt-2">
      <div
        className="mx-auto grid max-w-[1200px] gap-6"
        /* auto-FILL (not auto-fit) so the grid reserves empty tracks rather
           than stretching a single item across the whole row. minmax caps
           each column at 360px — prevents the "one minimized app" case from
           ballooning the card to 1200px wide and rendering a huge preview.
           With fewer apps than fit in a row, remaining tracks are empty;
           mx-auto keeps the populated cards visually centered-ish by the
           container's max-width + gap. */
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 360px))",
          justifyContent: "center",
        }}
      >
        {minimized.map((instance) => {
          const iconCfg = iconByWindowId.get(instance.config.id);
          const icon = iconCfg?.icon ?? instance.config.icon ?? (
            <div className="h-5 w-5 rounded bg-white/20" aria-hidden="true" />
          );
          return (
            <PreviewSlot
              key={instance.config.id}
              windowId={instance.config.id}
              title={
                instance.config.title ?? iconCfg?.label ?? instance.config.id
              }
              icon={icon}
              onSelect={() => onSelect(instance.config.id)}
              onClose={() => onCardClose(instance.config.id)}
              onMeasure={onMeasure}
              layoutVersion={minimized.length}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ==========================================================================
   CLOSEABLE CARD (mobile) — swipe up to close, iOS-style
   ==========================================================================

   Uses pointer events so we can cooperate with the horizontal scroll-snap
   carousel. Touch-action `pan-x` on the card tells the browser "only pan
   horizontally"; vertical finger motion is ours to handle in JS. We commit
   to an axis after a small threshold — if the user's first motion is mostly
   horizontal, we back off and let the browser scroll the carousel.
*/

const CLOSE_THRESHOLD_PX = 120;
const CLOSE_TRAVEL_FADE_PX = 300;

function CloseableCard({
  onClose,
  className,
  children,
}: {
  onClose: () => void;
  className?: string;
  /**
   * Render-prop child so consumers can thread the live drag offset into
   * inner components (e.g. PreviewSlot needs to republish its rect so the
   * portaled window preview tracks the wrapper during the swipe).
   */
  children: (dragY: number) => React.ReactNode;
}) {
  const [dragY, setDragY] = useState(0);
  const [closing, setClosing] = useState(false);
  const startYRef = useRef<number | null>(null);
  const startXRef = useRef<number | null>(null);
  const axisRef = useRef<"x" | "y" | null>(null);
  const pointerIdRef = useRef<number | null>(null);
  const tweenRafRef = useRef<number | null>(null);
  const dragYRef = useRef(0);
  dragYRef.current = dragY;

  // rAF tween so dragY advances on every frame. State-driven (not a CSS
  // transition) so the portaled window preview — which republishes its
  // rect whenever dragY changes — tracks the card through the whole
  // animation. Replaces the old `transition: transform 220ms` behavior.
  const tweenTo = useCallback((target: number, duration = 220) => {
    if (tweenRafRef.current !== null) {
      cancelAnimationFrame(tweenRafRef.current);
    }
    const from = dragYRef.current;
    if (duration <= 0 || from === target) {
      setDragY(target);
      return;
    }
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = from + (target - from) * eased;
      setDragY(v);
      if (t < 1) {
        tweenRafRef.current = requestAnimationFrame(step);
      } else {
        tweenRafRef.current = null;
      }
    };
    tweenRafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => {
    return () => {
      if (tweenRafRef.current !== null) {
        cancelAnimationFrame(tweenRafRef.current);
      }
    };
  }, []);

  const reset = useCallback(() => {
    startYRef.current = null;
    startXRef.current = null;
    axisRef.current = null;
    pointerIdRef.current = null;
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (closing) return;
    if (tweenRafRef.current !== null) {
      cancelAnimationFrame(tweenRafRef.current);
      tweenRafRef.current = null;
    }
    startYRef.current = e.clientY;
    startXRef.current = e.clientX;
    axisRef.current = null;
    pointerIdRef.current = e.pointerId;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (closing) return;
    if (pointerIdRef.current !== e.pointerId) return;
    if (startYRef.current === null || startXRef.current === null) return;

    const dy = e.clientY - startYRef.current;
    const dx = e.clientX - startXRef.current;

    if (axisRef.current === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        axisRef.current = "y";
        // Capture so we keep receiving moves even if the finger leaves the card
        e.currentTarget.setPointerCapture(e.pointerId);
      } else {
        // Horizontal — disengage so browser can scroll the carousel
        axisRef.current = "x";
        reset();
        return;
      }
    }

    if (axisRef.current === "y") {
      // Only track upward drags; allow a tiny downward nudge for rubber-band
      setDragY(Math.min(dy, 12));
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (pointerIdRef.current !== e.pointerId) return;
    if (axisRef.current === "y" && dragY <= -CLOSE_THRESHOLD_PX) {
      // Fly off the top and then tell the parent to actually close the window
      setClosing(true);
      tweenTo(-window.innerHeight, 220);
      window.setTimeout(() => onClose(), 220);
    } else {
      // Snap back
      tweenTo(0, 220);
    }
    startYRef.current = null;
    startXRef.current = null;
    axisRef.current = null;
    pointerIdRef.current = null;
  };

  const opacity = closing
    ? 0
    : Math.max(0, 1 + Math.min(dragY, 0) / CLOSE_TRAVEL_FADE_PX);

  return (
    <div
      className={className}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        transform: `translateY(${dragY}px)`,
        opacity,
        /* No CSS transition: dragY is driven directly by pointer moves and
           rAF tweens, so React updates the transform on each frame and the
           portaled preview republishes its rect in lockstep. */
        touchAction: "pan-x",
      }}
    >
      {children(dragY)}
    </div>
  );
}

/* ==========================================================================
   CAROUSEL (mobile)
   ========================================================================== */

function AppSwitcherCarousel({
  minimized,
  iconByWindowId,
  onSelect,
  onCardClose,
  onSlotsChange,
}: LayoutProps) {
  const onMeasure = useMeasureAggregator(onSlotsChange);
  const scrollerRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={scrollerRef}
      /* Scroller IS the flex row — cards are direct children so snap-align
         resolves reliably. Inline padding on the scroller itself provides
         the space needed for the first/last cards to sit centered. */
      className="flex flex-1 snap-x snap-mandatory items-center gap-4 overflow-x-auto overflow-y-hidden px-[16.665vw] py-4"
      style={{
        scrollbarWidth: "none",
        overscrollBehaviorX: "contain",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {/* Leading spacer — ensures the first card can scroll to dead-center
          even if scroll-padding is ignored by the browser. */}
      <div aria-hidden="true" className="flex-none" style={{ width: "0px" }} />
      {minimized.map((instance) => {
        const iconCfg = iconByWindowId.get(instance.config.id);
        const icon = iconCfg?.icon ?? instance.config.icon ?? (
          <div className="h-5 w-5 rounded bg-white/20" aria-hidden="true" />
        );
        return (
          <CloseableCard
            key={instance.config.id}
            /* snap-always forces the browser to settle on this card. No
                 per-card scale/opacity transforms — those caused the
                 active/inactive toggle to fire repeatedly during a drag and
                 visually jiggle the previews. Plain flex-none so cards track
                 the scroller 1:1 on the compositor. */
            className="flex-none snap-center snap-always"
            onClose={() => onCardClose(instance.config.id)}
          >
            {(dragY) => (
              <PreviewSlot
                windowId={instance.config.id}
                title={
                  instance.config.title ?? iconCfg?.label ?? instance.config.id
                }
                icon={icon}
                onSelect={() => onSelect(instance.config.id)}
                onClose={() => onCardClose(instance.config.id)}
                onMeasure={onMeasure}
                layoutVersion={minimized.length}
                liveOffset={dragY}
                /* Mini-phone portrait: ~2/3 of viewport, matches phone aspect */
                previewBoxStyle={{
                  width: "66.67vw",
                  height: "66.67vh",
                  aspectRatio: "auto",
                }}
              />
            )}
          </CloseableCard>
        );
      })}
      {/* Trailing spacer (symmetrical with the leading one). */}
      <div aria-hidden="true" className="flex-none" style={{ width: "0px" }} />
    </div>
  );
}
